import { Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { RequestBuilderService } from '../../../../shared/services/request-builder.service';
import { DatePickerModule } from 'primeng/datepicker';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ScheduledNotificationActionType, ScheduledNotificationStatus } from '../notification-queue-dialog/scheduled-notification';
import { TagComponent } from '../../../../shared/components/tag/tag.component';

@Component({
  selector: 'app-send-scheduled-notification-dialog',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, ButtonComponent, TextareaModule, InputTextModule, DatePickerModule, TagComponent],
  templateUrl: './send-scheduled-notification-dialog.component.html',
  styleUrl: './send-scheduled-notification-dialog.component.scss',
})
export class SendScheduledNotificationDialogComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly fb = inject(FormBuilder);

  loading = false;
  notificationForm!: FormGroup;
  minDate: Date | undefined;

  constructor() {
    // Only set minDate if not in view mode (allow past dates in view mode)
    // Set minDate to today at midnight to allow any time today to pass validation
    if (!this.config.data?.viewOnly) {
      const today = new Date();
      this.minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    }
    this.initForm();
  }

  ngOnInit() {
    if (this.config.data?.selectedNotification) {
      console.log(this.config.data.selectedNotification);

      // Extract just the date part from "2025-10-13T07:15:12.123" format
      // Split by 'T' to get the date portion, then create a Date object in local timezone at midnight
      const dueDateStr = this.config.data.selectedNotification.DueDate.split('T')[0];
      const [year, month, day] = dueDateStr.split('-').map(Number);
      const dueDate = new Date(year, month - 1, day, 0, 0, 0, 0); // Set to midnight (start of day)

      this.notificationForm.patchValue({
        title: this.config.data.selectedNotification.Title,
        body: this.config.data.selectedNotification.Body,
        dueDate: dueDate,
        dueTime: new Date(`${dueDateStr}T${this.config.data.selectedNotification.DueTime}`),
      });

      if (this.config.data?.viewOnly) {
        this.notificationForm.disable();
      }
    }
  }

  private initForm(): void {
    this.notificationForm = this.fb.group(
      {
        title: ['', [Validators.required, SendScheduledNotificationDialogComponent.noWhitespaceValidator]],
        body: ['', [Validators.required, SendScheduledNotificationDialogComponent.noWhitespaceValidator]],
        dueDate: ['', Validators.required],
        dueTime: ['', Validators.required],
      },
      { validators: this.dateTimeValidator() },
    );

    // Re-validate when date changes
    this.notificationForm.get('dueDate')?.valueChanges.subscribe(() => {
      this.notificationForm.get('dueTime')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * Validator to ensure the selected time is not in the past when today's date is selected
   */
  private dateTimeValidator() {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      // Skip validation in view mode
      if (this.config.data?.viewOnly) {
        return null;
      }

      const dueDateControl = formGroup.get('dueDate');
      const dueTimeControl = formGroup.get('dueTime');

      if (!dueDateControl?.value || !dueTimeControl?.value) {
        return null; // Let required validators handle empty values
      }

      const selectedDate = new Date(dueDateControl.value);
      const selectedTime = new Date(dueTimeControl.value);
      const now = new Date();

      // Check if selected date is today
      const isToday = selectedDate.getFullYear() === now.getFullYear() && selectedDate.getMonth() === now.getMonth() && selectedDate.getDate() === now.getDate();

      if (isToday) {
        // Compare time only
        const selectedHours = selectedTime.getHours();
        const selectedMinutes = selectedTime.getMinutes();
        const nowHours = now.getHours();
        const nowMinutes = now.getMinutes();

        const selectedTimeInMinutes = selectedHours * 60 + selectedMinutes;
        const nowTimeInMinutes = nowHours * 60 + nowMinutes;

        if (selectedTimeInMinutes <= nowTimeInMinutes) {
          return { pastTime: true };
        }
      }

      return null;
    };
  }

  private formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  confirmAction() {
    if (this.notificationForm.invalid) {
      this.notificationForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const time = this.formatTime(this.notificationForm.value.dueTime);
    const date = this.formatDate(this.notificationForm.value.dueDate);

    const body = {
      ...this.notificationForm.value,
      dueTime: time,
      dueDate: date,
    };
    if (this.config.data?.selectedNotification) {
      body['id'] = this.config.data.selectedNotification.Id;
      body['actionType'] = ScheduledNotificationActionType.Edit;
      body['status'] = this.config.data.selectedNotification.Status.id;
    } else {
      body['actionType'] = ScheduledNotificationActionType.Create;
      body['status'] = ScheduledNotificationStatus.Scheduled;
    }

    this.requestBuilder.patch(`notifications/schedule-notification-handler`, body).subscribe({
      next: res => {
        this.loading = false;
        this.ref.close({ confirmed: true });
      },
      error: error => {
        console.log(error);
        this.loading = false;
      },
    });
  }

  /**
   * Custom validator to check if the string is not just whitespace
   */
  static noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value && typeof control.value === 'string' && control.value.trim().length === 0) {
      return { whitespace: true };
    }
    return null;
  }
}
